import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { X, Check } from "lucide-react";
import { subscribe } from "../../api/newsletter";
import { trackNewsletterSignup } from "../../utils/analytics";
import { useLanguage } from "../../context/LanguageContext";
import useLockBodyScroll from "../../hooks/useLockBodyScroll";
import popupImage from "../../assets/images/newsletters.webp";
import {
  Overlay,
  Modal,
  ImageSide,
  FormSide,
  CloseBtn,
  Eyebrow,
  Title,
  Subtitle,
  Form,
  Input,
  SubmitBtn,
  Spinner,
  FinePrint,
  SuccessWrap,
  SuccessIcon,
  SuccessMsg,
  ErrorMsg,
  InfoMsg,
  LangSwitch,
  LangBtn,
  LangSep,
} from "./NewsletterPopup.styles";

const STORAGE_KEY = "777_newsletter_popup_shown";
const DELAY_MS = 3000;

const COPY = {
  sr: {
    eyebrow: "Ekskluzivna ponuda",
    title: "Pridruži se Lucky Club-u",
    titleAccent: "ostvari 10% popusta",
    subtitle:
      "Prijavi se i ostvari popust od 10% na svoju prvu porudžbinu. Bez spama, samo važne stvari.",
    namePh: "Ime",
    emailPh: "Email",
    submit: "Pridruži se Lucky Club-u",
    fine: (PrivacyEl, TermsEl) => (
      <>
        Prijavom prihvataš da primaš marketing poruke. Pogledaj našu {PrivacyEl}{" "}
        i {TermsEl} za detalje.
      </>
    ),
    privacy: "Politiku privatnosti",
    terms: "Uslove korišćenja",
    successTitle: "Uspešno",
    successMsg: "Proveri svoj email. Kod za 10% popusta je na putu.",
    alreadyMsg: "Već si na listi!",
    close: "Zatvori",
  },
  en: {
    eyebrow: "Exclusive offer",
    title: "Join the Lucky Club",
    titleAccent: "get 10% off",
    subtitle:
      "Sign up and get 10% off your first order. No spam, just the good stuff.",
    namePh: "Name",
    emailPh: "Email",
    submit: "Join the Lucky Club",
    fine: (PrivacyEl, TermsEl) => (
      <>
        By signing up, you agree to receive marketing emails. View our{" "}
        {PrivacyEl} and {TermsEl} for more info.
      </>
    ),
    privacy: "Privacy Policy",
    terms: "Terms of Service",
    successTitle: "You're In",
    successMsg: "Check your email for your 10% discount code.",
    alreadyMsg: "You're already on the list!",
    close: "Close",
  },
};

export default function NewsletterPopup() {
  const { lang, setLang } = useLanguage();
  const t = COPY[lang] || COPY.sr;

  const [visible, setVisible] = useState(false);
  useLockBodyScroll(visible);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | already | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return;
    const timer = setTimeout(() => setVisible(true), DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(STORAGE_KEY, "1");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    setErrorMsg("");
    try {
      const cleanEmail = email.trim();
      const cleanName = name.trim();
      const eventId = trackNewsletterSignup({
        email: cleanEmail,
        name: cleanName,
        source: "newsletter_popup",
      });
      await subscribe(cleanEmail, cleanName, eventId);
      setStatus("success");
      localStorage.setItem(STORAGE_KEY, "1");
      setTimeout(dismiss, 2500);
    } catch (err) {
      if (err.response?.data?.alreadySubscribed) {
        setStatus("already");
        localStorage.setItem(STORAGE_KEY, "1");
      } else {
        setErrorMsg(err.response?.data?.error || err.message);
        setStatus("error");
      }
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={dismiss}
        >
          <Modal
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
          >
            <ImageSide>
              <img src={popupImage} alt="" loading="lazy" />
            </ImageSide>

            <FormSide>
              <LangSwitch>
                <LangBtn
                  type="button"
                  $active={lang === "sr"}
                  onClick={() => setLang("sr")}
                >
                  SR
                </LangBtn>
                <LangSep>·</LangSep>
                <LangBtn
                  type="button"
                  $active={lang === "en"}
                  onClick={() => setLang("en")}
                >
                  EN
                </LangBtn>
              </LangSwitch>

              <CloseBtn onClick={dismiss} aria-label={t.close} type="button">
                <X size={18} />
              </CloseBtn>

              <AnimatePresence mode="wait">
                {status === "success" ? (
                  <SuccessWrap
                    key="success"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <SuccessIcon>
                      <Check size={24} />
                    </SuccessIcon>
                    <Title>{t.successTitle}</Title>
                    <SuccessMsg>{t.successMsg}</SuccessMsg>
                  </SuccessWrap>
                ) : (
                  <div key="form">
                    <Eyebrow>{t.eyebrow}</Eyebrow>
                    <Title>
                      {t.title}, <span>{t.titleAccent}</span>
                    </Title>
                    <Subtitle>{t.subtitle}</Subtitle>

                    <Form onSubmit={handleSubmit}>
                      <Input
                        type="text"
                        placeholder={t.namePh}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={status === "loading"}
                        autoComplete="given-name"
                      />
                      <Input
                        type="email"
                        placeholder={t.emailPh}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={status === "loading"}
                        autoComplete="email"
                      />
                      <SubmitBtn
                        type="submit"
                        disabled={status === "loading" || !email.trim()}
                      >
                        {status === "loading" ? <Spinner /> : t.submit}
                      </SubmitBtn>
                    </Form>

                    {status === "error" && <ErrorMsg>{errorMsg}</ErrorMsg>}
                    {status === "already" && <InfoMsg>{t.alreadyMsg}</InfoMsg>}

                    <FinePrint>
                      {t.fine(
                        <Link to="/privacy" onClick={dismiss}>
                          {t.privacy}
                        </Link>,
                        <Link to="/terms" onClick={dismiss}>
                          {t.terms}
                        </Link>,
                      )}
                    </FinePrint>
                  </div>
                )}
              </AnimatePresence>
            </FormSide>
          </Modal>
        </Overlay>
      )}
    </AnimatePresence>
  );
}
